import {
	GQLMerchTileFragment,
	GQLOrderItemFragment,
	GQLOrderItemPrintDecorationType,
	GQLOrderItemPrintSideType,
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import {
	Decoration,
	TeamNameOption,
	emptyDecorationMeta,
	getFirstSamedayColour,
	getSortedColours,
	getSortedDecorationTypes,
	getSortedSizes,
	getStockedColour,
} from '../..';
import { mapEnumValue } from '../../../utils';
import { Merch } from '../schema';
import { DesignParamKeys, DesignParams } from './getDesignParams';

interface InitialMerchProps {
	store?: GQLStoreFragment | null;
	product: GQLProductFragment;
	referenceMerch?: GQLMerchTileFragment | null;
	orderItem?: GQLOrderItemFragment | null;
	params: DesignParams;
}

export function getInitialMerch({ product, referenceMerch, orderItem, params, store }: InitialMerchProps): Merch {
	const hasDTG = product.sides.some(side => side.availableDecorations.includes(GQLProductSideAvailableDecorationType.Dtg));
	const sameday = !!params.sameday && hasDTG && !!product.isSameday;
	const stockedColour = sameday ? getFirstSamedayColour(product) : getStockedColour(product);
	const colourCode = params.colour;
	const paramColour = getSortedColours(product.colours)?.find(({ colour }) => colour.code.toLowerCase() === colourCode?.toLowerCase());
	const firstColour = getSortedColours(product.colours)[0];
	const defaultColour = (store ? firstColour : paramColour || stockedColour || firstColour)?.colour.code;
	const referenceColour = !!referenceMerch
		? referenceMerch.preferedColourCode
		: !!orderItem
		? orderItem.colour?.colour.code
		: defaultColour;

	const defaultVariant = getSortedSizes(product)[0]?.variant;

	const sizing =
		orderItem?.sizes?.map(s => ({
			size: s.size?.code || '',
			quantity: s.quantity,
		})) ||
		params[DesignParamKeys.Sizes]?.filter(s => getSortedSizes(product).find(size => size.variant.size.code === s.size)) ||
		(store && defaultVariant
			? [
					{
						size: defaultVariant.size.code,
						quantity: 1,
					},
			  ]
			: []) ||
		[];

	const teamname = orderItem?.teamnames?.structure || referenceMerch?.teamnames?.structure || [];
	const decorations = product.sides.map(side => {
		const sortedDecs = getSortedDecorationTypes(side);
		let defaultType = side.availableDecorations.find(d => d === params.decoration) || sortedDecs[0];
		if (!!referenceMerch) {
			const matchingDecoration = referenceMerch?.decorations?.find(d => d.side?.side === side.side);

			const merchDec: Decoration = {
				side: side.side,
				type: matchingDecoration?.type || defaultType,
				isMainDecoration: matchingDecoration?.isMainDecoration || false,
				meta: {
					...emptyDecorationMeta,
					height: matchingDecoration?.height || 0,
					width: matchingDecoration?.width || 0,
					left: matchingDecoration?.left || 0,
					top: matchingDecoration?.top || 0,
					designId: matchingDecoration?.design?.id || null,
					stitches: matchingDecoration?.stitches || 0,
					useSuppliedEmbFile: matchingDecoration?.useSuppliedEmbFile || !!store,
					colours: matchingDecoration?.colours || [],
					requestedColours: matchingDecoration?.requestedColours || matchingDecoration?.decorationThreads?.structure?.length || 0,
				},
				artwork: new Blob([]),
			} as Decoration;
			return merchDec;
		} else if (!!orderItem) {
			const matchingPrint = orderItem?.prints?.find(
				p => mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, p.side) === side.side,
			);
			const printDec: Decoration = {
				side: side.side,
				type:
					mapEnumValue(GQLOrderItemPrintDecorationType, GQLProductSideAvailableDecorationType, matchingPrint?.decoration) ||
					defaultType,
				meta: {
					...emptyDecorationMeta,
					height: matchingPrint?.height || 0,
					width: matchingPrint?.width || 0,
					left: matchingPrint?.left || 0,
					top: matchingPrint?.top || 0,
					stitches: matchingPrint?.stitches || 0,
					useSuppliedEmbFile: !!store,
					colours: (matchingPrint?.colours?.structure?.filter(Boolean) as any[]) || [],
					requestedColours: matchingPrint?.requestedColours,
				},
				artwork: new Blob([]),
			};
			return printDec;
		}

		let type = defaultType;
		if (sameday && sortedDecs.includes(GQLProductSideAvailableDecorationType.Dtg)) {
			type = GQLProductSideAvailableDecorationType.Dtg;
		}

		return {
			side: side.side,
			type,
			meta: {
				...emptyDecorationMeta,
				useSuppliedEmbFile: !!store,
			},
			artwork: new Blob([]),
		};
	});

	const merch: Merch = {
		sameday,
		dirty: false,
		name: !!referenceMerch ? referenceMerch?.name || 'Untitled Merch' : 'Untitled Merch',
		scene: orderItem?.sceneFile?.file?.url || referenceMerch?.sceneFile?.file?.url || referenceMerch?.scene || '',
		sizing,
		colour: product.colours.find(c => c.colour.code === referenceColour)?.colour.code || defaultColour,
		decorations,
		teamNames: teamname?.length
			? {
					teamNameId: '',
					available: [],
					selected: teamname.map(t => ({
						size: t?.size || '',
						decorations,
						quantity: t?.quantity || 1,
						inputs:
							t?.inputs?.map(i => ({
								id: (i?.id || '') as any as TeamNameOption,
								name: i?.name || '',
							})) || [],
					})),
			  }
			: params[DesignParamKeys.TeamnameId]
			? {
					teamNameId: params[DesignParamKeys.TeamnameId],
					available: [],
					selected: [],
			  }
			: undefined,
	};
	return merch;
}
